import { Logger } from '@cgs/shared';
import { Container, IDisposable, SoundState } from '@cgs/wge';
import { IconDrawOrderCalculator } from '../reels_engine/icon_draw_order_calculator';
import { IGameParams } from '../reels_engine/interfaces/i_game_params';
import {
  T_AbstractIconResourceProvider,
  T_BaseSlotSoundController,
  T_GameConfigController,
  T_IconEnumeratorComponent,
  T_IFadeReelsProvider,
  T_IIconDrawOrderCalculator,
  T_InitialReelsComponent,
  T_IReelsConfigProvider,
  T_ISlotGameEngineProvider,
  T_ISlotPrimaryActionsProvider,
  T_IStartSlotActionProvider,
  T_IStopSlotActionProvider,
  T_IWinLineActionProvider,
} from '../type_definitions';
import { BaseSlotGame, REQUIRED_COMPONENTS_PROVIDER_TOKEN } from './base_slot_game';
import { ReelsEngineSlotSoundController } from './common/reels_engine_slot_sound_controller';
import { SlotSoundConfigEntry, SlotSoundType } from './common/slot_sound_config_entry';
import { GameConfigController } from './components/game_config_controller';
import { IconEnumeratorComponent } from './components/icon_enumerator_component';
import { InitialReelsComponent } from './components/initial-reels/initial_reels_component';
import { ReelsConfigComponent } from './components/reels_config_component';
import { ReelsEngineComponent } from './components/reels_engine_component';
import { ReelsEngineSlotPrimaryActionsProvider } from './components/reels_engine_slot_primary_actions_provider';
import { SingleSceneIconResourceProvider } from './components/single_scene_icon_resource_provider';
import { SpinActionComponent } from './components/spin_action_component';
import { StopActionComponent } from './components/stop_action_component';
import { FadeReelsProvider } from './components/win_lines/fade_reels_provider';
import { WinLineActionProvider } from './components/win_lines/win_line_action_provider';

export class BaseReelsSlotGame extends BaseSlotGame implements IDisposable {
  constructor(container: Container, gameParams: IGameParams) {
    super(container, gameParams);

    this.container.registerValue(REQUIRED_COMPONENTS_PROVIDER_TOKEN, [T_GameConfigController], {
      multi: true,
    });
  }

  initComponentContainer(): void {
    this.registerAsSingleInstance((_c) => {
      Logger.Debug('load IconDrawOrderCalculator');
      return new IconDrawOrderCalculator();
    }, T_IIconDrawOrderCalculator);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load IconEnumeratorComponent');
      return new IconEnumeratorComponent(c);
    }, T_IconEnumeratorComponent);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load ReelsConfigComponent');
      return new ReelsConfigComponent(c);
    }, T_IReelsConfigProvider);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load GameConfigController');
      return new GameConfigController(c);
    }, T_GameConfigController);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load ReelsEngineComponent');
      return new ReelsEngineComponent(c);
    }, T_ISlotGameEngineProvider);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load SlotAnimationComponent');
      return new ReelsEngineSlotPrimaryActionsProvider(c);
    }, T_ISlotPrimaryActionsProvider);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load SpinActionProvider');
      return new SpinActionComponent(c);
    }, T_IStartSlotActionProvider);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load StopActionProvider');
      return new StopActionComponent(c, false);
    }, T_IStopSlotActionProvider);

    const iconsLimit: Map<number, number> = new Map<number, number>();
    iconsLimit.set(0, 2);
    iconsLimit.set(1, 2);
    iconsLimit.set(2, 0);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load InitialReelsComponent');
      return new InitialReelsComponent(c, iconsLimit);
    }, T_InitialReelsComponent);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load WinLineActionProvider');
      return new WinLineActionProvider(c);
    }, T_IWinLineActionProvider);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load FadeReelsProvider');
      return new FadeReelsProvider(c);
    }, T_IFadeReelsProvider);

    this.registerAsSingleInstance((container) => {
      Logger.Debug('load IconResourceProvider');
      return new SingleSceneIconResourceProvider(container);
    }, T_AbstractIconResourceProvider);

    this.registerAsSingleInstance((c) => {
      Logger.Debug('load SlotSoundController');
      return new ReelsEngineSlotSoundController(c, [
        new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Playing, 'show'),
        new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Stopped, 'hide'),
        new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Paused, 'pause'),
      ]);
    }, T_BaseSlotSoundController);

    super.initComponentContainer();
  }
}
